import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import './index.less';

import { AgentModel, AgentState, DataInterfaceType } from '../../../../../gamma-api';
import { getAgentPath, getAgentToolsetsPath } from '../../../../utils/pathUtils';
import IbCard from '../../../../components/common/IbCard';
import IbContextMenu, { IIbContextMenuItem } from '../../../../components/common/IbContextMenu';
import IbButton from '../../../../components/common/IbButton';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import ModelTag from '../../../../components/ModelTag';
import CardContent, { ICardContentItem } from '../../../../components/CardContent';
import { formatDateFull, getTranslateFunc } from '../../../../utils/stringUtil';
import { getToolsetItemName, getToolsetTypeIconName } from '../../AgentPage/ToolsetsTab/utils';
import Avatar, { AvatarName } from '../../../../components/common/Avatar';
import IbSwitch from '../../../../components/common/IbSwitch';
import ExportButton from '../../../../components/ExportButton';
import ImportUpload from '../../../../components/ImportUpload';

import {
  DISABLED_CLASS_NAME,
  EXTRA_CLASS_NAME,
  FOOTER_CLASS_NAME,
  MAIN_CLASS_NAME,
  TOOLSETS_PAGE_SIZE,
  TRANSLATION_GROUP_PREFIX,
} from './const';

interface IAgentCardProps {
  agent: AgentModel;
  searchText: string;
  onEditAgent: () => void;
  onDeleteAgent: () => void;
  onAgentClick: () => void;
  onAgentStateToggle: () => Promise<void>;
}

const AgentCard: React.FC<IAgentCardProps> = ({
  agent,
  searchText,
  onEditAgent,
  onDeleteAgent,
  onAgentClick,
  onAgentStateToggle,
}) => {
  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);
  const { i18n } = useTranslation();

  const { push } = useHistory();

  const [contextMenuVisible, setContextMenuVisible] = useState(false);

  const goToAgent = () => {
    push(getAgentPath(agent.settings.interface.type, agent.folder_id, agent.agent_id));
  };

  const onImportFileFinish = async () => {
    goToAgent();
  };

  const contextMenuItems = [
    {
      icon: <IbIcon iconName="efferent-three" size={20} />,
      text: translate('Context: Export'),
      render: (child) => (
        <ExportButton.AgentExport agentId={agent.agent_id} interfaceType={agent.settings.interface.type}>
          {child}
        </ExportButton.AgentExport>
      ),
    },
    {
      icon: <IbIcon iconName="afferent-three" size={20} />,
      text: translate('Context: Import'),
      render: (child) => (
        <ImportUpload.AgentImport
          agentId={agent.agent_id}
          folderId={agent.folder_id}
          interfaceType={agent.settings.interface.type}
          onFinish={onImportFileFinish}
        >
          {child}
        </ImportUpload.AgentImport>
      ),
    },
    {
      icon: <IbIcon iconName="edit" size={20} />,
      onSelect: onEditAgent,
      text: translate('Context: Edit'),
    },
    {
      icon: <IbIcon iconName="delete" size={20} />,
      onSelect: onDeleteAgent,
      text: translate('Context: Delete'),
    },
  ] as IIbContextMenuItem[];

  const renderExtra = () => {
    const onContextMenuClose = () => setContextMenuVisible(false);
    const onMenuButtonClick = () => setContextMenuVisible(true);
    return (
      <div className={EXTRA_CLASS_NAME} onClick={(e) => e.stopPropagation()}>
        <IbSwitch checked={agent.state === AgentState.Enabled} onChange={onAgentStateToggle} />
        <IbContextMenu menuItems={contextMenuItems} visible={contextMenuVisible} onClose={onContextMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" size={20} />} type="icon" onClick={onMenuButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  const onPromptClick = () => {
    goToAgent();
  };

  const onShowAllToolsetsButtonClick = () => {
    push(getAgentToolsetsPath(agent.settings.interface.type, agent.folder_id, agent.agent_id));
  };

  const renderContent = () => {
    const items: ICardContentItem[] = agent.toolsets.slice(0, TOOLSETS_PAGE_SIZE).map((toolset) => ({
      key: toolset.toolset_id,
      iconName: getToolsetTypeIconName(toolset.type),
      displayName: getToolsetItemName(toolset),
    }));

    return (
      <CardContent
        instructions={agent.settings.instructions}
        items={items}
        itemsLabel={translate('Tools: All', { count: agent.toolsets.length })}
        onShowAllItems={onShowAllToolsetsButtonClick}
        onShowFullInstructions={onPromptClick}
      />
    );
  };

  const renderFooter = () => {
    return (
      <div className={FOOTER_CLASS_NAME}>
        <IbTypography.Paragraph disabled type="descriptor">
          {translate('Modified on', { date: formatDateFull(agent.modified_on, i18n.language) })}
        </IbTypography.Paragraph>
        <ModelTag modelName={agent.settings.llm.model_name} />
      </div>
    );
  };

  const getDisplayName = () => {
    if (agent.settings.interface.type === DataInterfaceType.Chat) {
      return agent.settings.widget.display_name;
    }

    return undefined;
  };

  const renderAvatar = () => {
    if (agent.settings.interface.type === DataInterfaceType.Api) return;
    return <Avatar avatarName={agent.settings.widget.avatar.name as AvatarName} size={48} />;
  };

  const classes = [MAIN_CLASS_NAME];
  agent.state === AgentState.Disabled && classes.push(DISABLED_CLASS_NAME);

  return (
    <div className={classes.join(' ')}>
      <IbCard
        avatar={renderAvatar()}
        displayName={getDisplayName()}
        extra={renderExtra()}
        footer={renderFooter()}
        searchText={searchText}
        title={agent.name}
        onClick={onAgentClick}
      >
        {renderContent()}
      </IbCard>
    </div>
  );
};

export default AgentCard;
